import { FC, useEffect, useRef, useState } from 'react';
import {
  DataRoomActivityEventType,
  DataRoomDownloadScope,
  SpaceKind,
  VisitorTimelineDay,
  VisitorTimelineEntry,
} from 'shared';
import { twJoin } from 'tailwind-merge';

import activityTimelineIcon from '../../../../assets/images/data-room-activity/activity-timeline.svg';
import timelineChevronIcon from '../../../../assets/images/data-room-activity/timeline-chevron.svg';
import timelineDownloadedIcon from '../../../../assets/images/data-room-activity/timeline-downloaded.svg';
import timelineOpenedIcon from '../../../../assets/images/data-room-activity/timeline-opened.svg';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { LoadMoreButton } from '../../../../components/load-more-button/index';
import { HeightReveal } from '../../../../components/motion/index';
import { StringKey } from '../../../../lang/index';
import { afterScroll, scrollParentOf } from '../../hooks/use-browser-page-reveal.hook';
import { useViewSpaceFile } from '../../hooks/use-view-space-file.hook';
import { chunksOf } from '../../utils/chunks.util';
import { ActivityVisitorTimeline, VISITOR_TIMELINE_DAYS } from '../hooks/use-activity-visitor.hook';
import { ActivityTimelineDaysPlaceholder } from './activity-loading-placeholders';
import { DataRoomTag } from './data-room-tag';

const EVENT: Record<DataRoomActivityEventType, { icon: string; label: StringKey }> = {
  [DataRoomActivityEventType.DOWNLOAD]: { icon: timelineDownloadedIcon, label: 'DOWNLOADED' },
  [DataRoomActivityEventType.VIEW]: { icon: timelineOpenedIcon, label: 'OPENED' },
};

/** 20.1.0 §7: a bulk download logs one entry per file, tagged with what was asked for. */
const SCOPE_LABEL: Record<DataRoomDownloadScope, StringKey> = {
  [DataRoomDownloadScope.DATA_ROOM_BULK]: 'AS_PART_OF_A_DATA_ROOM_DOWNLOAD',
  [DataRoomDownloadScope.FOLDER_BULK]: 'AS_PART_OF_A_FOLDER_DOWNLOAD',
  [DataRoomDownloadScope.INDIVIDUAL]: 'INDIVIDUALLY',
};

const DAY_MS = 86_400_000;

// The pause between days of one page rising in, and between events of a day being opened.
const DAY_CASCADE_MS = 50;
const ENTRY_CASCADE_MS = 30;
// Events that each wait for the one before; the rest come in with the last, so a busy day is
// not slow to fill.
const ENTRY_CASCADE_STEPS = 8;

// A day opens in a time that grows with what it holds, so a long day does not race past the eye.
const openSeconds = (entries: number): number => Math.min(0.6, 0.3 + entries * 0.008);

// "Show less": the pages past the first fold away in this long before they leave the list.
const FOLD_SECONDS = 0.35;

const prefersReducedMotion = (): boolean =>
  globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

const utcDay = (offsetDays: number): string =>
  new Date(Date.now() - offsetDays * DAY_MS).toISOString().slice(0, 10);

/**
 * One day's heading on the timeline — 20.1.4 §8.
 *
 * The day already arrived cut in UTC, the same timezone the window and the engagement chart are
 * cut in, so this formats the date it was given and never re-reads it in the viewer's zone: a
 * Tuesday-evening view must not sit under Wednesday on one screen and Tuesday on another. Today
 * and yesterday are named by the same UTC clock.
 */
const DayHeading: FC<{ day: string }> = ({ day }) => {
  const date = new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
    year: 'numeric',
  });
  const relative: null | StringKey =
    day === utcDay(0) ? 'TODAY' : day === utcDay(1) ? 'YESTERDAY' : null;

  return (
    <span className="text-label-sm font-bold uppercase tracking-[1px] text-gray-700">
      {relative && (
        <>
          <AppFormattedMessage id={relative} />
          {', '}
        </>
      )}
      {date}
    </span>
  );
};

const timeLabel = (occurredAt: string): string =>
  new Date(occurredAt).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });

const TimelineEntry: FC<{ entry: VisitorTimelineEntry; onOpenFile: () => void }> = ({
  entry,
  onOpenFile,
}) => (
  <div className="flex min-w-0 items-center gap-3">
    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-gray-100 bg-white">
      <img alt="" className="size-4" src={EVENT[entry.type].icon} />
    </span>
    <span className="flex min-w-0 flex-col">
      <span className="truncate text-sm font-[450] text-gray-700">
        <AppFormattedMessage id={EVENT[entry.type].label} />
        {/* The file opens as it does in Workspaces: shown in a tab, or saved where it cannot be. */}
        {entry.fileName && (
          <>
            {' '}
            <button className="text-brand-700 hover:underline" onClick={onOpenFile} type="button">
              {entry.fileName}
            </button>
          </>
        )}
        {entry.scope && entry.scope !== DataRoomDownloadScope.INDIVIDUAL && (
          <span className="text-gray-500">
            {' ('}
            <AppFormattedMessage id={SCOPE_LABEL[entry.scope]} />
            {')'}
          </span>
        )}
      </span>
      <span className="truncate text-label-sm font-medium text-gray-400">
        {timeLabel(entry.occurredAt)}
      </span>
    </span>
    {/* Every entry says which room it happened in, the way the Files tab marks a file's room. */}
    <span className="ml-auto flex min-w-0 max-w-[40%] shrink-0 justify-end">
      <DataRoomTag name={entry.dataRoomName} />
    </span>
  </div>
);

const TimelineDay: FC<{
  // When the day rises in after the ones above it: a page of days arrives as a cascade.
  enterDelayMs: number;
  isOpen: boolean;
  onOpenFile: (entry: VisitorTimelineEntry) => void;
  onToggle: () => void;
  timelineDay: VisitorTimelineDay;
}> = ({ enterDelayMs, isOpen, onOpenFile, onToggle, timelineDay }) => {
  // A day open from the start shows its events as they are; one the reader opens lets them
  // follow one another in, so the card rising in does not carry a second cascade inside it.
  const [isCascading, setIsCascading] = useState(false);
  const seconds = openSeconds(timelineDay.entries.length);

  return (
    <div
      className={twJoin(
        // Background, outline and the bottom padding all change with the height and over the same
        // time, so nothing below the day moves before its events do. The outline is a ring: it
        // takes no room, and turns transparent rather than disappearing.
        'flex animate-reveal-up flex-col rounded-md pt-3 ring-1 ring-inset transition-[background-color,box-shadow,padding] ease-in-out motion-reduce:animate-none motion-reduce:transition-none',
        isOpen ? 'bg-gray-50 pb-0 ring-transparent' : 'bg-white pb-3 ring-gray-100',
      )}
      style={{ animationDelay: `${enterDelayMs}ms`, transitionDuration: `${seconds}s` }}
    >
      <button
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between px-4"
        onClick={() => {
          setIsCascading(true);
          onToggle();
        }}
        type="button"
      >
        <DayHeading day={timelineDay.day} />
        <img
          alt=""
          className={twJoin(
            'size-5 transition-transform ease-in-out motion-reduce:transition-none',
            isOpen && 'rotate-180',
          )}
          src={timelineChevronIcon}
          style={{ transitionDuration: `${seconds}s` }}
        />
      </button>
      {/* The day's events grow out of its heading and fold back into it. The spacing rides inside,
        so it grows and folds with them. */}
      <HeightReveal duration={seconds} isShown={isOpen}>
        <div className="px-0.5 pb-0.5 pt-3">
          <div className="flex flex-col rounded-md bg-white px-2 pb-3 pt-3 shadow-sm">
            {timelineDay.entries.map((entry, index) => (
              <div
                className={twJoin(
                  'flex flex-col',
                  isCascading && 'animate-reveal-up motion-reduce:animate-none',
                )}
                key={entry.id}
                style={
                  isCascading
                    ? {
                        animationDelay: `${Math.min(index, ENTRY_CASCADE_STEPS) * ENTRY_CASCADE_MS}ms`,
                      }
                    : undefined
                }
              >
                {index > 0 && <span className="ml-[18px] h-6 w-px bg-gray-200" />}
                <TimelineEntry entry={entry} onOpenFile={() => onOpenFile(entry)} />
              </div>
            ))}
          </div>
        </div>
      </HeightReveal>
    </div>
  );
};

type ActivityTimelineProps = {
  days: VisitorTimelineDay[];
  // What an empty window says: nothing here, or nothing in this room.
  emptyLabel: StringKey;
  // The profile is on its way for another window or room: the pill waits with it.
  isReloading: boolean;
  timeline: ActivityVisitorTimeline;
};

/**
 * The Activity Timeline — 20.1.4 §8 — five days at a time with every entry they hold.
 *
 * Each page past the first sits in a panel of its own, so "Show less" can fold those panels away
 * before the pages leave the list: the view first glides up to the heading, then the folded days
 * close below it and the pill rises after them, instead of the list snapping short under the eye.
 * The next page's days pulse where they will land while it loads, then rise in over them.
 */
export const ActivityTimeline: FC<ActivityTimelineProps> = ({
  days,
  emptyLabel,
  isReloading,
  timeline,
}) => {
  const headingRef = useRef<HTMLDivElement>(null);
  const foldTimer = useRef<number | undefined>(undefined);
  const isFoldingRef = useRef(false);
  const [isFolding, setIsFolding] = useState(false);
  const viewFile = useViewSpaceFile();
  // Days the reader opened or closed; any other day follows the default, where the newest is open.
  const [toggledDays, setToggledDays] = useState<Record<string, boolean>>({});

  useEffect(() => () => window.clearTimeout(foldTimer.current), []);

  const showLess = async (): Promise<void> => {
    // A second click while the view is still gliding up would fold twice.
    if (isFoldingRef.current) return;

    isFoldingRef.current = true;

    const heading = headingRef.current;
    const scroller = scrollParentOf(heading);
    const reduceMotion = prefersReducedMotion();

    if (
      heading &&
      scroller &&
      heading.getBoundingClientRect().top < scroller.getBoundingClientRect().top
    ) {
      heading.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });

      if (!reduceMotion) await afterScroll(scroller);
    }

    if (reduceMotion) {
      timeline.showLess();
      isFoldingRef.current = false;

      return;
    }

    setIsFolding(true);
    window.clearTimeout(foldTimer.current);
    foldTimer.current = window.setTimeout(() => {
      timeline.showLess();
      setIsFolding(false);
      isFoldingRef.current = false;
    }, FOLD_SECONDS * 1000);
  };

  // One preview for the whole timeline: the file opens as it does in Workspaces.
  const openFile = (entry: VisitorTimelineEntry): void =>
    void viewFile({
      fileId: entry.fileId,
      space: { id: entry.dataRoomId, kind: SpaceKind.DATA_ROOM },
    });

  const renderDay = (timelineDay: VisitorTimelineDay, index: number) => {
    const isOpen = toggledDays[timelineDay.day] ?? index === 0;

    return (
      <TimelineDay
        enterDelayMs={(index % VISITOR_TIMELINE_DAYS) * DAY_CASCADE_MS}
        isOpen={isOpen}
        key={timelineDay.day}
        onOpenFile={openFile}
        onToggle={() => setToggledDays((current) => ({ ...current, [timelineDay.day]: !isOpen }))}
        timelineDay={timelineDay}
      />
    );
  };

  const [firstPage = [], ...laterPages] = chunksOf(days, VISITOR_TIMELINE_DAYS);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex scroll-mt-4 items-center gap-2 py-4" ref={headingRef}>
        <img alt="" className="size-6" src={activityTimelineIcon} />
        <h3 className="text-base font-semibold text-gray-700">
          <AppFormattedMessage id="ACTIVITY_TIMELINE" />
        </h3>
      </div>
      {days.length ? (
        <div className="flex flex-col">
          <div className="flex flex-col gap-4">{firstPage.map(renderDay)}</div>
          {laterPages.map((page, pageIndex) => (
            <HeightReveal
              duration={FOLD_SECONDS}
              isShown={!isFolding}
              key={`page-${pageIndex + 1}`}
            >
              <div className="flex flex-col gap-4 pt-4">
                {page.map((timelineDay, index) =>
                  renderDay(timelineDay, (pageIndex + 1) * VISITOR_TIMELINE_DAYS + index),
                )}
              </div>
            </HeightReveal>
          ))}
          {timeline.isLoadingMore && (
            <div className="flex flex-col gap-4 pt-4">
              <ActivityTimelineDaysPlaceholder count={VISITOR_TIMELINE_DAYS} />
            </div>
          )}
        </div>
      ) : (
        // §9's two empty states: this visitor has done nothing *here*, or nothing in the window.
        <span className="py-6 text-sm font-[450] text-gray-400">
          <AppFormattedMessage id={emptyLabel} />
        </span>
      )}
      {(timeline.nextPageSize > 0 || timeline.canShowLess) && (
        <div className="flex justify-center">
          <LoadMoreButton
            count={timeline.nextPageSize}
            isLoading={timeline.isLoadingMore || isReloading || isFolding}
            onLoadMore={timeline.loadMore}
            onShowLess={() => void showLess()}
            showLess={timeline.nextPageSize === 0}
          />
        </div>
      )}
    </div>
  );
};
